import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styles from "./Footer.module.css";
import FabButton from "../Fab/Fab";
import { useTranslation } from "react-i18next";
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Link as MuiLink, 
  IconButton, 
  Divider, 
  Button
} from "@mui/material";
import { 
  Facebook, 
  Twitter, 
  Linkedin, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin, 
  ArrowRight, 
  Globe 
} from "lucide-react";

const Footer = () => {
  const location = useLocation();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("profile")));
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage;
  const isApp = location.pathname
    .toLowerCase()
    .includes(`/${currentLang}/app`.toLowerCase());

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("profile")));
  }, [location]);

  const currentYear = new Date().getFullYear();

  if (isApp) return null;

  return (
    <Box component="footer" className={styles.footer}>
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={4} className={styles.footerMainContent}>
          {/* Company Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={styles.footerHeading}>
              Quantum Billing
            </Typography>
            <Typography variant="body2" className={styles.footerDescription}>
              {t("Advanced ERP and billing solutions for modern businesses. Streamline your operations and boost productivity.")}
            </Typography>
            <Box className={styles.socialIcons}>
              <IconButton aria-label="Facebook" color="primary" size="small">
                <Facebook size={20} />
              </IconButton>
              <IconButton aria-label="Twitter" color="primary" size="small">
                <Twitter size={20} />
              </IconButton>
              <IconButton aria-label="LinkedIn" color="primary" size="small">
                <Linkedin size={20} />
              </IconButton>
              <IconButton aria-label="Instagram" color="primary" size="small">
                <Instagram size={20} />
              </IconButton>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={styles.footerHeading}>
              {t("Quick Links")}
            </Typography>
            <ul className={styles.footerLinks}>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/`} underline="hover">{t("Home")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/about`} underline="hover">{t("About Us")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/services`} underline="hover">{t("Services")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/pricing`} underline="hover">{t("Pricing")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/contact`} underline="hover">{t("Contact")}</MuiLink>
              </li>
            </ul>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={styles.footerHeading}>
              {t("Our Services")}
            </Typography>
            <ul className={styles.footerLinks}>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href="#" underline="hover">{t("ERP Solutions")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href="#" underline="hover">{t("Billing Management")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href="#" underline="hover">{t("Inventory Control")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href="#" underline="hover">{t("Financial Analytics")}</MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href="#" underline="hover">{t("Cloud Solutions")}</MuiLink>
              </li>
            </ul>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={styles.footerHeading}>
              {t("Contact Us")}
            </Typography>
            <Box className={styles.contactInfo}>
              <Box className={styles.contactItem}>
                <MapPin size={18} className={styles.contactIcon} />
                <Typography variant="body2">
                  123 Business Avenue, Tech Park, CA 94103
                </Typography>
              </Box>
              <Box className={styles.contactItem}>
                <Phone size={18} className={styles.contactIcon} />
                <MuiLink 
                  href="tel://+1361-405-5427" 
                  underline="hover"
                  className={styles.contactLink}
                >
                  +1 361-405-5427
                </MuiLink>
              </Box>
              <Box className={styles.contactItem}>
                <Mail size={18} className={styles.contactIcon} />
                <MuiLink 
                  href="mailto:info@quantumbilling.com" 
                  underline="hover"
                  className={styles.contactLink}
                >
                  info@quantumbilling.com
                </MuiLink>
              </Box>
              <Box className={styles.contactItem}>
                <Globe size={18} className={styles.contactIcon} />
                <MuiLink 
                  href="https://www.quantumbilling.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  className={styles.contactLink}
                >
                  www.quantumbilling.com
                </MuiLink>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider className={styles.footerDivider} />

        {/* Newsletter Section */}
        <Box className={styles.newsletterSection}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" className={styles.newsletterHeading}>
                {t("Subscribe to Our Newsletter")}
              </Typography>
              <Typography variant="body2">
                {t("Stay updated with our latest news and special offers.")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={styles.subscribeForm}>
                <input 
                  type="email" 
                  placeholder={t("Your Email Address")} 
                  className={styles.subscribeInput}
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  className={styles.subscribeButton}
                >
                  {t("Subscribe")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider className={styles.footerDivider} />

        {/* Copyright Section */}
        <Box className={styles.copyrightSection}>
          <Typography variant="body2" align="center">
            &copy; {currentYear} Quantum Billing | {t("Made with")} &hearts; {t("by")} {t("Quantum Information Technology Limited (QITL)")}
          </Typography>
          <Box className={styles.footerBottomLinks}>
            <MuiLink href="#" underline="hover" className={styles.bottomLink}>
              {t("Privacy Policy")}
            </MuiLink>
            <MuiLink href="#" underline="hover" className={styles.bottomLink}>
              {t("Terms of Service")}
            </MuiLink>
            <MuiLink href="#" underline="hover" className={styles.bottomLink}>
              {t("Cookie Policy")}
            </MuiLink>
          </Box>
        </Box>
      </Container>
      {user && <FabButton />}
    </Box>
  );
};

export default Footer;
